import React, { useEffect, useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import requestApi from "../../apirest/index";
import Loading from "../../shared/components/Loading/Loading";
import formatBRL from "../../shared/utils/formatBRL";
import categoryLabel from "../../shared/utils/categoryLabel";
import "./BulkPricing.css";

const PAGE_SIZE = 10;

function round2(value) {
  return Math.round(value * 100) / 100;
}

function calcNewPrices(custo, markupFull, markupSale, isSale) {
  const price = round2(custo * (1 + markupFull / 100));

  if (!isSale) {
    return { price, salePrice: price, discount: 0, sale: false };
  }

  const salePrice = round2(custo * (1 + markupSale / 100));
  const discount = price > 0 ? round2((1 - salePrice / price) * 100) : 0;
  const sale = discount > 0.01;
  return { price, salePrice, discount, sale };
}

const BulkPricing = () => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [costsByProduct, setCostsByProduct] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState(() => new Set());
  const [markupFull, setMarkupFull] = useState(60);
  const [markupSale, setMarkupSale] = useState(20);
  const [isSale, setIsSale] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState({ done: 0, total: 0 });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [productsResp, totalCostResp] = await Promise.all([
          requestApi(`/products`, {}, "get"),
          requestApi(`/totalCost`, {}, "get"),
        ]);
        setProducts(productsResp?.data?.doc || []);

        const costs = {};
        (totalCostResp?.data?.doc || []).forEach((doc) => {
          const productId = doc.product?._id || doc.product;
          if (productId && costs[productId] === undefined) {
            costs[productId] = doc.price;
          }
        });
        setCostsByProduct(costs);
      } catch (error) {
        toast.error("Erro ao carregar produtos/custos.");
      }
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toUpperCase();
    return products.filter(
      (p) =>
        p.code?.toUpperCase().includes(term) ||
        p.name?.toUpperCase().includes(term),
    );
  }, [products, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage],
  );

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    setSelected((prev) => {
      const allSelected = filtered.every((p) => prev.has(p._id));
      if (allSelected) return new Set();
      return new Set(filtered.map((p) => p._id));
    });
  };

  const applyMarkup = async (targetIds) => {
    const eligible = targetIds.filter((id) => costsByProduct[id] !== undefined);
    const skipped = targetIds.length - eligible.length;

    if (eligible.length === 0) {
      toast.warning("Nenhum produto selecionado tem custo cadastrado.");
      return;
    }

    if (
      !window.confirm(
        `Isso vai atualizar preço cheio e preço com desconto de ${eligible.length} produto(s)` +
          (skipped > 0 ? ` (${skipped} sem custo cadastrado será ignorado)` : "") +
          `. Confirma?`,
      )
    ) {
      return;
    }

    setSaving(true);
    setSavingProgress({ done: 0, total: eligible.length });

    let done = 0;
    const failed = [];

    for (const id of eligible) {
      const product = products.find((p) => p._id === id);
      const custo = costsByProduct[id];
      const { price, salePrice, discount, sale } = calcNewPrices(
        custo,
        markupFull,
        markupSale,
        isSale,
      );
      try {
        await requestApi(
          `/products`,
          { product: { ...product, price, salePrice, discount, sale } },
          "patch",
        );
        done += 1;
      } catch (error) {
        failed.push(product.code);
      }
      setSavingProgress({ done, total: eligible.length });
    }

    setSaving(false);

    if (failed.length === 0) {
      toast.success(`${done} produto(s) atualizado(s) com sucesso.`);
    } else {
      toast.error(`${done} atualizado(s), falhou em: ${failed.join(", ")}`);
    }

    // refresh local product list with new price/salePrice so a novo preview reflita o estado salvo
    setProducts((prev) =>
      prev.map((p) => {
        if (!eligible.includes(p._id)) return p;
        const custo = costsByProduct[p._id];
        const { price, salePrice, discount, sale } = calcNewPrices(
          custo,
          markupFull,
          markupSale,
          isSale,
        );
        return { ...p, price, salePrice, discount, sale };
      }),
    );
  };

  const selectedCount = selected.size;
  const eligibleFilteredCount = filtered.filter(
    (p) => costsByProduct[p._id] !== undefined,
  ).length;

  return (
    <div className="cp-page">
      <ToastContainer />
      <div className="cp-topbar">
        <div>
          <p className="cp-topbar-breadcrumb">Produtos</p>
          <h1 className="cp-topbar-title">Precificação em massa</h1>
        </div>
      </div>

      <div className="bp-controls cp-card">
        <div className="bp-controls-row">
          <div className="cp-field bp-field">
            <label className="cp-label">Markup preço cheio (%)</label>
            <input
              type="number"
              className="cp-input"
              value={markupFull}
              onChange={(e) => setMarkupFull(parseFloat(e.target.value) || 0)}
            />
          </div>
          <div className="cp-field bp-field bp-field-checkbox">
            <label className="cp-label bp-checkbox-label">
              <input
                type="checkbox"
                checked={isSale}
                onChange={(e) => setIsSale(e.target.checked)}
              />
              Item em promoção
            </label>
          </div>
          {isSale && (
            <div className="cp-field bp-field">
              <label className="cp-label">Markup preço com desconto (%)</label>
              <input
                type="number"
                className="cp-input"
                value={markupSale}
                onChange={(e) => setMarkupSale(parseFloat(e.target.value) || 0)}
              />
            </div>
          )}
          <div className="bp-actions">
            <button
              type="button"
              className="cp-btn cp-btn--secondary"
              disabled={saving || selectedCount === 0}
              onClick={() => applyMarkup(Array.from(selected))}
            >
              Aplicar aos selecionados ({selectedCount})
            </button>
            <button
              type="button"
              className="cp-btn cp-btn--primary"
              disabled={saving || filtered.length === 0}
              onClick={() => applyMarkup(filtered.map((p) => p._id))}
            >
              Aplicar a todos ({eligibleFilteredCount})
            </button>
          </div>
        </div>
        {saving && (
          <p className="bp-progress">
            Aplicando {savingProgress.done}/{savingProgress.total}...
          </p>
        )}
      </div>

      <div className="cp-field bp-search-field">
        <input
          type="text"
          className="cp-input"
          placeholder="Buscar produto por nome ou código..."
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="cp-card bp-table-card">
        {paged.length > 0 ? (
          <div className="bp-table-scroll">
            <table className="bp-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        filtered.length > 0 &&
                        filtered.every((p) => selected.has(p._id))
                      }
                      onChange={toggleSelectAllFiltered}
                    />
                  </th>
                  <th>Código</th>
                  <th>Produto</th>
                  <th>Custo</th>
                  <th>Preço cheio (atual → novo)</th>
                  <th>Preço com desconto (atual → novo)</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((p) => {
                  const custo = costsByProduct[p._id];
                  const hasCustom = custo !== undefined;
                  const preview = hasCustom
                    ? calcNewPrices(custo, markupFull, markupSale, isSale)
                    : null;
                  return (
                    <tr key={p._id} className={!hasCustom ? "bp-row-no-cost" : ""}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.has(p._id)}
                          onChange={() => toggleSelected(p._id)}
                        />
                      </td>
                      <td>{p.code}</td>
                      <td>
                        <div className="bp-product">
                          <img
                            src={p?.variants?.[0]?.pictures?.[0]?.url}
                            className="bp-product-img"
                            alt={p.name}
                          />
                          <div>
                            <p className="bp-product-name">{p.name}</p>
                            <p className="bp-product-category">
                              {categoryLabel(p.category)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>{formatBRL(custo)}</td>
                      <td>
                        {formatBRL(p.price)}
                        {preview && (
                          <>
                            {" → "}
                            <strong>{formatBRL(preview.price)}</strong>
                          </>
                        )}
                      </td>
                      <td>
                        {formatBRL(p.salePrice)}
                        {preview && (
                          <>
                            {" → "}
                            <strong>
                              {isSale
                                ? formatBRL(preview.salePrice)
                                : "sem promoção"}
                            </strong>
                          </>
                        )}
                        {!hasCustom && (
                          <span className="bp-no-cost-tag"> sem custo</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          !loading && <p className="bp-empty">Nenhum produto encontrado.</p>
        )}
      </div>

      {totalPages > 1 && (
        <div className="bp-pagination">
          <button
            type="button"
            className="cp-btn cp-btn--secondary"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              type="button"
              className={`bp-pagination-page${page === currentPage ? " bp-pagination-page--active" : ""}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            type="button"
            className="cp-btn cp-btn--secondary"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            ›
          </button>
        </div>
      )}

      {loading && <Loading overlay />}
    </div>
  );
};

export default BulkPricing;
